import { ReactElement, useEffect, useState } from 'react';
import { VStack } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { useSchoolVouchers, useVouchers } from 'hooks/api/backend/managament/getB2BUsers';
import { Pagination } from 'components/common/Table/Pagination';
import { Filters } from 'components/common/Table/Filters';
import { VoucherData } from 'components/common/Table/types/Column';
import { Table } from 'components/common/Table/Table';
import { ProtectedRoutes } from 'types/Routes';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import { OrganizationManagementLayout } from 'components/OrganizationManagement/OrganizationManagementLayout';
import { formattedDateWithMonthName } from 'utils/formattedDate';
import { ActionButton } from 'components/atoms/managament/ActionButton';
import { Voucher, VoucherType } from '@novaheal/types';
import { VoucherCell } from 'components/common/Table/Cells/VoucherCell';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { toastContent } from 'config/toast';
import { ManagementStatusType } from 'types/StatusTypes';
import { UsageCell } from 'components/common/Table/Cells/UsageCell';
import { createColumns } from 'utils/tableHelper';
import { OptionWithCheckmark } from 'components/common/Table/FilterOptions/OptionWithCheckmark';
import storage from 'utils/storage';

import { QueryErrorState } from 'components/common/QueryErrorState';

export const VoucherDashboard = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const { data: allVouchersData } = useSchoolVouchers();
  const modalService = useModalService();

  const selectedSchool = storage.getTenant();
  const [selectedVoucherType, setSelectedVoucherType] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const toast = useNovaToast();

  const {
    data: vouchersData,
    refetch,
    isLoading,
    isError,
    error,
  } = useVouchers({
    voucherType: selectedVoucherType,
    page: currentPage,
  });

  useEffect(() => {
    const vId = searchParams.get('vId');

    if (vId) {
      setSelectedVoucherType(vId);
    }
  }, [searchParams]);

  const handleVoucherChange = (voucherId: string): void => {
    setSelectedVoucherType(voucherId);
    resetPagination();
  };

  const handleManageUsers = (voucherId: string): void => {
    window.location.href = `${ProtectedRoutes.ORG_MANAGEMENT_USER_DASHBOARD}?vId=${voucherId}`;
  };

  const resetPagination = (): void => {
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };
  const voucherColumns = createColumns<VoucherData>()({
    shortCode: {
      header: 'Lizenz-ID',
      element: VoucherCell,
      getCellProps: (row: VoucherData) => ({
        label: row.validUntil || '',
      }),
      sortDisabled: true,
    },
    validUntil: { header: 'Ablaufdatum', sortDisabled: true },
    usageCount: {
      header: 'Anzahl der Nutzungen',
      element: UsageCell,
      getCellProps: (row: VoucherData) => ({
        allowedUsage: row.allowedUsage.toString(),
        usedUsage: row.usageCount.toString(),
      }),
      sortDisabled: true,
    },
    actionItem: { header: 'Aktion', sortDisabled: true },
  });

  const mappedVouchers = vouchersData?.data?.map((voucher: Voucher) => ({
    ...voucher,
    validUntil: formattedDateWithMonthName(voucher.validUntil),
    usageCount: voucher.timesUsed,

    actionItem: (
      <ActionButton
        title="Verwalten"
        onClick={() =>
          handleManageUsers(String(voucher.type) !== 'SINGLE_USE' ? voucher.id : 'SINGLE_USE')
        }
      />
    ),
  }));

  const handleInviteClick = (): void => {
    if (!selectedSchool) {
      toast.show(toastContent.management[ManagementStatusType.SELECT_SCHOOL]);
      return;
    }

    const schoolVouchers = allVouchersData?.data;

    if (!schoolVouchers || schoolVouchers.length === 0) {
      toast.show(toastContent.management[ManagementStatusType.NO_VOUCHER_FOUND]);
      return;
    }

    modalService.show(ModalVariant.ADMIN_INVITE, {
      selectedVoucherId: undefined,
      allVouchers: schoolVouchers?.map((voucher: Voucher) => ({
        ...voucher,
        validUntil: voucher.validUntil || new Date(0), // Default to epoch if undefined
        status: String(voucher.status),
        type: String(voucher.type), // Ensure type is a string
      })),
    });
  };

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  return (
    <OrganizationManagementLayout
      pageTitle="Lizenzen"
      tabTitle="Lizenzen"
      handleInviteClick={handleInviteClick}
    >
      <VStack align="left" bg="background.primary" w="full" gap="l" borderRadius={{ md: '2xl' }}>
        <Filters
          filters={[
            [
              {
                id: 'voucher',
                placeholder: 'Alle Lizenzen',
                showPlaceholderAsFirstOption: true,
                value: selectedVoucherType,
                element: OptionWithCheckmark,
                options: [
                  { id: String(VoucherType.MULTI_USE), label: 'Mehrfachgebrauch' },
                  { id: String(VoucherType.SINGLE_USE), label: 'Einmalgebrauch' },
                ],
                disabled: !selectedSchool,
                onChange: handleVoucherChange,
              },
            ],
          ]}
        />
        <Table<VoucherData>
          columns={voucherColumns}
          data={mappedVouchers || []}
          isDataLoading={isLoading}
        />
        {!!(vouchersData?.totalPages && vouchersData?.totalPages > 1) && (
          <Pagination
            totalPages={vouchersData?.totalPages || 0}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </VStack>
    </OrganizationManagementLayout>
  );
};
